// Client-side pooling for the Scorecards tab -- a supervisor can
// multi-select several weeks/months at once (see ReportingPage.jsx's own
// Scorecards section), and the table always shows ONE flat per-agent row
// pooled across whichever buckets are currently selected, not one row group
// per bucket (unlike AHT by account per rep's own per-period breakdown --
// see ahtByRepPooling.js). Reacting instantly to the period/agent selection
// with no re-fetch is why this pooling happens here rather than server-side,
// same reasoning ahtByRepPooling.js's own module comment gives.
//
// Same "reconstruct each row's total ms from its already-divided average
// times its count, sum those, divide once" rule as poolAgentRows -- never an
// average of the per-bucket averages, which would weight a slow/quiet bucket
// the same as a busy one. Adherence pools the same way, from the raw
// scheduled/adherent minutes the route sends alongside the ratio (see
// bucketAdherence's own module comment in repositories/reporting.js) --
// pooling the already-divided `adh` percentages together would be the exact
// averaging mistake every other number on this page already avoids.
//
// poolScorecardRows's OUTPUT carries the same raw-sum fields its INPUT does
// (totalContacts/emailAht/supportEmailContacts/supportEmailAht/
// adhScheduledMinutes/adhAdherentMinutes/cxhubScheduledMinutes)
// specifically so the Total row can pool the already-pooled per-agent rows
// a second time with this exact same function, rather than needing a
// separate grand-total reducer.
//
// cxhubScheduledMinutes (2026-10-14) is simpler than the adherence fields
// above -- it is never a ratio of anything, just a total, so there is only
// one sum to carry, no companion "already-divided" value to avoid
// re-averaging.
//
// utilization (2026-10-14, Patrick's ask -- "who works most efficiently in
// between contacts") is handle time ÷ CXHub scheduled time, i.e. what
// fraction of an agent's completed scheduled hours actually went to email
// contacts. It needs no accumulator of its own: the ratio's numerator
// (emailMsSum + supportMsSum) and denominator (cxhubScheduledSum) are both
// already being summed above for Email AHT and CXHub Scheduled Hours, so
// it's computed straight from those at the end, same "sum first, divide
// once" rule -- never an average of each bucket's own already-divided
// percentage. null (not a fabricated 0%) when cxhubScheduledSum is 0 --
// Patrick confirmed 0 CXHub Scheduled Hours means "not scheduled in CXHub
// this period" (an IT/BSA/QA admin account, a brand-new agent, a gap in
// Schedule Hub's own roster), not "scheduled for zero hours and therefore
// infinitely busy." A genuinely idle-but-scheduled agent (hours > 0, no
// contacts) correctly reads 0%, not null -- that's real signal, not a gap.

// rows: every selected bucket's `agents` array, already filtered to the
// selected buckets and flattened together (callers decide the two
// selections; this function just pools whatever rows it's given). Returns
// one row per distinct agent, in the SAME shape as one bucket's own row, so
// the result can be pooled again (for a Total row) or rendered directly.
export function poolScorecardRows(rows) {
  const byAgent = new Map()
  for (const row of rows) {
    const acc = byAgent.get(row.agent) ?? {
      agent: row.agent,
      contactsSum: 0, emailMsSum: 0,
      supportContactsSum: 0, supportMsSum: 0,
      adhScheduledSum: 0, adhAdherentSum: 0,
      cxhubScheduledSum: 0,
    }
    if (row.totalContacts > 0) {
      acc.contactsSum += row.totalContacts
      acc.emailMsSum += row.emailAht * row.totalContacts
    }
    if (row.supportEmailContacts > 0) {
      acc.supportContactsSum += row.supportEmailContacts
      acc.supportMsSum += row.supportEmailAht * row.supportEmailContacts
    }
    acc.adhScheduledSum += row.adhScheduledMinutes ?? 0
    acc.adhAdherentSum += row.adhAdherentMinutes ?? 0
    acc.cxhubScheduledSum += row.cxhubScheduledMinutes ?? 0
    byAgent.set(row.agent, acc)
  }

  return [...byAgent.values()].map((acc) => ({
    agent: acc.agent,
    totalContacts: acc.contactsSum,
    emailAht: acc.contactsSum > 0 ? acc.emailMsSum / acc.contactsSum : null,
    supportEmailContacts: acc.supportContactsSum,
    supportEmailAht: acc.supportContactsSum > 0 ? acc.supportMsSum / acc.supportContactsSum : null,
    adhScheduledMinutes: acc.adhScheduledSum,
    adhAdherentMinutes: acc.adhAdherentSum,
    adh: acc.adhScheduledSum > 0 ? acc.adhAdherentSum / acc.adhScheduledSum : null,
    cxhubScheduledMinutes: acc.cxhubScheduledSum,
    utilization: acc.cxhubScheduledSum > 0 ? (acc.emailMsSum + acc.supportMsSum) / (acc.cxhubScheduledSum * 60000) : null,
  }))
}

// The Total row across every agent -- poolScorecardRows again, over the
// per-agent rows it already produced, all folded onto one synthetic agent
// key so a single row comes back out.
export function poolScorecardTotal(pooledAgentRows) {
  return poolScorecardRows(pooledAgentRows.map((r) => ({ ...r, agent: '__total__' })))[0] ?? {
    agent: '__total__', totalContacts: 0, emailAht: null,
    supportEmailContacts: 0, supportEmailAht: null,
    adhScheduledMinutes: 0, adhAdherentMinutes: 0, adh: null,
    cxhubScheduledMinutes: 0, utilization: null,
  }
}
